import React from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import PageHero from '../components/PageHero'

export default function About() {
  return (
    <>
      <Navbar />
      <PageHero
        title="About SISDI"
        subtitle="Empowering children across Africa through school-based football and education."
        variant="about"
        fadeClass="fade-in"
      />

      <main>
        {/* Mission & Vision */}
        <section className="section fade-in">
          <div className="container">
            <div className="mission-vision-grid">
              <div style={{ textAlign: 'center' }}>
                <div className="icon-badge" style={{ margin: '0 auto 1rem', width: '56px', height: '56px' }}>
                  <i className="bi bi-bullseye" style={{ fontSize: '1.5rem', color: 'var(--color-primary)' }}></i>
                </div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.4rem' }}>Our Mission</h3>
                <p>To provide every child with access to quality football development in schools,
                integrating sport and education to nurture well-rounded individuals
                who excel both on the field and in life.</p>
              </div>

              <div>
                <img
                  src="/images/about-mission.jpeg"
                  className="img-fluid rounded shadow"
                  alt="SISDI mission in action"
                  style={{ borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)', width: '100%' }}
                />
              </div>

              <div style={{ textAlign: 'center' }}>
                <div className="icon-badge" style={{ margin: '0 auto 1rem' }}>
                  <i className="bi bi-eye" style={{ fontSize: '1.5rem', color: 'var(--color-primary)' }}></i>
                </div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem', fontSize: '1.4rem' }}>Our Vision</h3>
                <p>To create a future where every child, regardless of background,
                has access to quality football development opportunities in schools.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="section bg-light fade-in">
          <div className="container">
            <div className="section-header">
              <span className="eyebrow">How We Started</span>
              <h2>Our <span className="text-accent">Story</span></h2>
              <div className="section-divider"></div>
            </div>
            <div className="story-body">
              <p>Across Africa, millions of children grow up with talent,
              ambition, and dreams, but without the systems needed to
              nurture them safely and holistically. For many, football
              is seen as a distraction from education, while for others,
              poverty makes both quality schooling and structured football
              development feel out of reach, especially for girls,
              who are frequently excluded altogether.</p>

              <p style={{ marginTop: '1.5rem' }}>Too often, young players are forced to choose between the
              classroom and the football field, a choice no child should
              have to make. Yet the reality is clear: football and
              education are strongest when they work together.</p>

              <p style={{ marginTop: '1.5rem' }}>We saw the consequences of these gaps. Children chasing football
              dreams without guidance, dropping out of school, and losing
              direction when those dreams don&apos;t materialize. We also saw
              untapped potential — young people who, with the right support,
              could thrive in many areas of life.</p>

              <p style={{ marginTop: '1.5rem' }}>By integrating structured football training with learning, life skills,
              and career exposure, we create a safe, inclusive environment where
              children are prepared for life both on and off the pitch.
              This approach protects futures, expands opportunities for both boys and girls,
              and builds stronger communities for generations to come.</p>

              <p style={{ marginTop: '1.5rem' }}>Our story is one of belief that when education and sport work hand in hand,
              children don&apos;t just dream of a better future; they are equipped to build it.</p>
            </div>
          </div>
        </section>

        {/* What We Do */}
        <section className="section fade-in">
          <div className="container">
            <div className="section-header">
              <span className="eyebrow">Programs &amp; Initiatives</span>
              <h2><span className="text-accent">What We Do</span></h2>
              <div className="section-divider"></div>
            </div>

            <div className="grid-3 fade-in-stagger" style={{ marginTop: '3rem' }}>
              <div className="card">
                <img src="/images/prog-football.jpeg" className="card-img-top" alt="Football programs" />
                <div className="card-body">
                  <h4 className="card-title" style={{ textAlign: 'center' }}>School-based Football Development</h4>
                  <p className="card-text" style={{ textAlign: 'center' }}>We deliver structured football programs directly within schools, ensuring children can develop their
                  talent in a safe environment while staying fully engaged in their education.</p>
                </div>
              </div>

              <div className="card">
                <img src="/images/prog-education.jpeg" className="card-img-top" alt="Education support" />
                <div className="card-body">
                  <h4 className="card-title" style={{ textAlign: 'center' }}>Education, Life Skills, and Career Exposure</h4>
                  <p className="card-text" style={{ textAlign: 'center' }}>Beyond football, we sensitize and equip young players with life skills, mentorship, and
                  exposure to diverse career pathways, preparing them for success in any future they choose.</p>
                </div>
              </div>

              <div className="card">
                <img src="/images/prog-life-skills.jpeg" className="card-img-top" alt="Life skills" />
                <div className="card-body">
                  <h4 className="card-title" style={{ textAlign: 'center' }}>Inclusive Access and Equal Opportunity</h4>
                  <p className="card-text" style={{ textAlign: 'center' }}>We create opportunities for both boys and girls, especially in underserved communities, by removing
                  financial and social barriers to participation and promoting equality through sport.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Accordion */}
        <section className="section bg-light fade-in">
          <div className="container" style={{ maxWidth: '800px' }}>
            <div className="section-header">
              <span className="eyebrow">What Drives Us</span>
              <h2>Our <span className="text-accent">Values</span></h2>
              <div className="section-divider"></div>
            </div>

            <div className="values-accordion" style={{ marginTop: '3rem' }}>
              <div className="value-item value-integrity">
                <button className="value-toggle">
                  <span className="value-label">
                    <span className="value-icon">🎓</span>
                    S &mdash; School-based Football
                  </span>
                  <span className="chevron">&#8964;</span>
                </button>
                <div className="value-content">
                  <p>We place schools at the heart of our work, ensuring football
                  supports education and creates structure, discipline, and balance in every child&apos;s life.</p>
                </div>
              </div>

              <div className="value-item value-community">
                <button className="value-toggle">
                  <span className="value-label">
                    <span className="value-icon">🤝</span>
                    I &mdash; Inclusion and Equality
                  </span>
                  <span className="chevron">&#8964;</span>
                </button>
                <div className="value-content">
                  <p>We champion equal access for boys and girls, removing social, cultural,
                  and economic barriers to participation in football.</p>
                </div>
              </div>

              <div className="value-item value-holistic">
                <button className="value-toggle">
                  <span className="value-label">
                    <span className="value-icon">🧠</span>
                    S &mdash; Skills for Life
                  </span>
                  <span className="chevron">&#8964;</span>
                </button>
                <div className="value-content">
                  <p>We equip children with practical life and career skills
                  that prepare them for opportunities beyond football.</p>
                </div>
              </div>

              <div className="value-item value-partnership">
                <button className="value-toggle">
                  <span className="value-label">
                    <span className="value-icon">🧭</span>
                    D &mdash; Discipline and Direction
                  </span>
                  <span className="chevron">&#8964;</span>
                </button>
                <div className="value-content">
                  <p>We instill discipline, purpose, and responsible decision-making,
                  guiding young players toward positive futures.</p>
                </div>
              </div>

              <div className="value-item value-impact">
                <button className="value-toggle">
                  <span className="value-label">
                    <span className="value-icon">&#9889;</span>
                    I &mdash; Impact Beyond the Pitch
                  </span>
                  <span className="chevron">&#8964;</span>
                </button>
                <div className="value-content">
                  <p>Our success is measured by transformed lives, stronger communities,
                  and empowered futures &mdash; not just results on the field.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join CTA */}
        <section className="final-cta-section fade-in">
          <div className="container text-center">
            <span className="eyebrow" style={{ color: 'var(--color-accent-light)' }}>Be Part of the Story</span>
            <h2 className="cta-text" style={{ marginTop: '0.75rem' }}>
              <span>Join the Movement</span>
            </h2>
            <p className="lead">Support our mission or get involved today.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2rem' }}>
              <a href="/donate" className="btn btn-accent btn-lg">
                <i className="bi bi-heart-fill"></i> Donate Today
              </a>
              <a href="/get-involved" className="btn btn-outline btn-lg">Get Involved</a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
